<template>
  <div :style="computedColors">
    <div class="min-h-screen">
      <RouterView />
    </div>
    <GlobalFooter />
    <GlobalToast />
  </div>
</template>

<script setup lang="ts">
  import { onBeforeMount, onMounted, onUnmounted, ref, watchEffect } from 'vue';
  import { computed } from 'vue';
  import { RouterView } from 'vue-router';

  import router from '@/router';
  import { useCartStore } from '@/stores/cart';
  import { useCheckoutStore } from '@/stores/checkoutStore';
  import { useGeneralStore } from '@/stores/general';
  import { useWebsocketStore } from '@/websocket/websocket';

  import GlobalFooter from './components/globals/GlobalFooter.vue';
  import GlobalToast from './components/ui/GlobalToast.vue';
  import { getTextColorForBackground } from './utils/getTextColorForBackground';
  import EventDetailView from './views/EventDetailView.vue';
  import EventListView from './views/EventListView.vue';

  const primaryColor = ref('#8F57E0');
  const primaryDarkColor = ref('color-mix(in srgb, var(--color-primary) 90%, #000 10%)');

  const computedColors = computed(() => {
    if (!primaryColor.value) {
      return {};
    }

    const primaryTextColor = getTextColorForBackground(primaryColor.value);

    // NOTE calculated css properties are not recalculated when their dependencies change, so you must update these also.
    return {
      '--color-primary': primaryColor.value,
      '--color-primary-text':
        primaryTextColor === 'light'
          ? 'var(--color-surface-light)'
          : 'var(--color-surface-dark)',
      '--color-primary-dark': primaryDarkColor.value,
    };
  });

  watchEffect(() => {
    Object.entries(computedColors.value).forEach(([key, value]) => {
      document.documentElement.style.setProperty(key, value);
    });
  });

  ///////// Routing adjustments //////////
  // Route adjustments inserted here to ensure it runs early in the app lifecycle, also this is where the RouterView component is located. We can't put it in src/router/index.ts since the store isnt initialized yet when it is executed.

  const generalStore = useGeneralStore();
  generalStore.fetchGeneral().then(() => {
    let reloadNeeded = false;    

    // Event short_urls
    if (generalStore.general?.short_urls) {
      Object.entries(generalStore.short_urls()).forEach(([shortUrl, eventId]) => {
        // Check if the shortUrl already exists in the router
        const existingShortUrlRoute = router.getRoutes().find(route => route.path === `/${shortUrl}`);
        if (existingShortUrlRoute) {
          console.warn(`Route for shortUrl '${shortUrl}' already exists. Skipping.`); // TODO DELETE
          return;
        }

        router.addRoute({
          path: `/${shortUrl}`,
          name: `${shortUrl}`,
          alias: `/${shortUrl}`,
          component: EventDetailView, // Use the default component if available src/views/EventDetailView.vue
          props: { shortURL: `${shortUrl}` },
        });
      });
      reloadNeeded = true;
    }

    // Category short_urls
    if (generalStore.general?.categories) {
      generalStore.general.categories.forEach((category) => {
        if (category.short_url) {
          // Check if the shortUrl already exists in the router
          const existingShortUrlRoute = router.getRoutes().find(route => route.path === `/${category.short_url}`);
          if (existingShortUrlRoute) {
            return;
          }
          
          router.addRoute({
            path: `/${category.short_url}`,
            name: `alias-category-${category.short_url}`,
            alias: `/${category.short_url}`,
            component: EventListView, // Use the default component if available
            props: { categoryShortURL: `${category.short_url}` },
          });
        }
      });
      reloadNeeded = true;
    }

    //Never reload during checkout or receipt page, it causes bugs
    if(router.currentRoute.value.name?.toString().startsWith('checkout') ){
      reloadNeeded = false;
    }

    // Reprocess the current path after adding aliases
    if (reloadNeeded) {
      const currentPath = router.currentRoute.value.fullPath;
      router.replace('/').then(() => {
        router.replace(currentPath);
      });
    }
  });
  
   //restore connection on app start, if there was one
  const websocket = useWebsocketStore();
  websocket.restoreConnection();
  //If we are not in checkout, remove empty event message
  let cleanUpEmptyEvents = false;
  if(!router.currentRoute.value.name?.toString().startsWith('checkout') && !router.currentRoute.value.name?.toString().startsWith('receipt') ){
    websocket.send('cleanupeventsbasket', {  }, undefined);
    cleanUpEmptyEvents = true;
    console.log('Sent cleanupeventsbasket message to websocket on app start');
  }

  //Trigger cleanup on route change, if we are not in checkout and something is in cart
  router.beforeEach((to, from, next) => {
    const cartStore = useCartStore();
    if(!to.name?.toString().startsWith('checkout') && !to.name?.toString().startsWith('receipt') && cartStore.items.length > 0){
      websocket.send('cleanupeventsbasket', {  }, undefined);
      console.log('Sent cleanupeventsbasket message to websocket on route change');
      const tempCurrentOrderData = cartStore.currentOrderData;
      console.log('Saved current order data before cleanup:', tempCurrentOrderData);
      //In case we move from checkout-information or checkout-tickets, we need to save the current order data
      cartStore.loadCartData(cleanUpEmptyEvents);
      if(from.name == 'checkout-information' || from.name == 'checkout-tickets') {
        cartStore.currentOrderData = tempCurrentOrderData;
      }
    }
    next();
  });

  ///////// Inactivity timeout //////////
  const INACTIVITY_TIMEOUT_MS = 20 * 60 * 1000; // 20 minutes
  //const INACTIVITY_TIMEOUT_MS = 1 * 60 * 1000; // 1 minute for testing
  let inactivityTimer: ReturnType<typeof setTimeout> | undefined = undefined;
  let siteEntryTimer: ReturnType<typeof setTimeout> | undefined = undefined;
  let inactivityRouteUnsubscribe: (() => void) | undefined = undefined;
  const ACTIVITY_EVENTS = ['mousemove', 'keydown', 'mousedown', 'click', 'scroll', 'touchstart', 'touchmove'] as const;

  function clearInactivityTimers() {
    if (inactivityTimer !== undefined) { clearTimeout(inactivityTimer); inactivityTimer = undefined; }
    if (siteEntryTimer !== undefined) { clearTimeout(siteEntryTimer); siteEntryTimer = undefined; }
  }

  function handleInactivity() {
    const currentRoute = router.currentRoute.value;
    const currentRouteName = currentRoute.name?.toString() ?? '';

    // Rule 3: receipt page — should never fire here as timers are cleared on entry, but guard anyway
    if (currentRouteName === 'checkout-receipt') return;

    const cartStore = useCartStore();
    const checkoutStore = useCheckoutStore();

    // Rule 5: capture active event ID BEFORE clearing checkout data
    const activeEventId = checkoutStore.id;

    // Reset stores
    console.log('User inactive for 20 minutes – resetting session and cart data.');
    cartStore.resetCart();

    // Set bypassPaymentGuard BEFORE router.push so the onBeforeRouteLeave guard
    // in CheckoutCancelPaymentModal lets navigation through without showing the modal.
    checkoutStore.bypassPaymentGuard = true;
    checkoutStore.showCancelPaymentModal = false;
    checkoutStore.clearCheckoutData();

    // Clear all cached data from storage
    localStorage.clear();
    sessionStorage.clear();

    // Close websocket connection
    websocket.close();

    console.log('User inactive for 20 minutes – session cleared.');
    console.log('Current route:', currentRoute.fullPath);
    console.log('route name:', currentRouteName);

    // Rules 1 & 2: do not navigate if already on frontpage or event detail page
    const isOnFrontpage = currentRouteName === 'event-list' || currentRoute.path === '/';
    const isOnEventDetail = currentRouteName === 'event-detail' || currentRouteName.startsWith('alias-');
    if (isOnFrontpage || isOnEventDetail) return;

    // Rule 5: navigate to the active event's detail page if one was in progress
    if (activeEventId) {
      router.push({ name: 'event-detail', params: { id: activeEventId } });
    } else {
      // Default: navigate to frontpage
      router.push('/');
    }
  }

  function resetInactivityTimer() {
    if (inactivityTimer !== undefined) clearTimeout(inactivityTimer);
    inactivityTimer = setTimeout(handleInactivity, INACTIVITY_TIMEOUT_MS);
  }

  onMounted(() => {
    ACTIVITY_EVENTS.forEach(event => {
      window.addEventListener(event, resetInactivityTimer, { passive: true });
    });
    resetInactivityTimer();
    // Hard session timer: fires once 20 minutes after entering the site, regardless of activity.
    siteEntryTimer = setTimeout(handleInactivity, INACTIVITY_TIMEOUT_MS);

    // Rule 3: clear both timers when user reaches the receipt page
    inactivityRouteUnsubscribe = router.afterEach((to) => {
      if (to.name?.toString() === 'checkout-receipt') {
        clearInactivityTimers();
      }
    });
  });

  onUnmounted(() => {
    ACTIVITY_EVENTS.forEach(event => {
      window.removeEventListener(event, resetInactivityTimer);
    });
    clearInactivityTimers();
    inactivityRouteUnsubscribe?.();
  });

  onBeforeMount(() => {
    const cartStore = useCartStore();
    if(cartStore.firstBasketLoad){
      console.log('Loading cart data on app start');
      cartStore.loadCartData(cleanUpEmptyEvents);
      cartStore.firstBasketLoad = false;
    }
    
  });
</script>
